import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, getConnectionDetails, isUsingFallbackClient } from '../lib/supabase';
import { User, Session, AuthError } from '@supabase/supabase-js';

interface UserProfile {
  age?: number;
  location?: string;
  mentalHealthGoals: string[];
  concerns: string[];
  communicationStyle: 'direct' | 'gentle' | 'encouraging';
  crisisContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  privacySettings: {
    dataAnalysis: boolean;
    aiPersonalization: boolean;
    anonymousUsage: boolean;
  };
  phq2Score?: number;
  created_at?: string;
}

interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isEmailVerified: boolean;
  hasCompletedOnboarding: boolean;
  isAdmin: boolean;
  isDemoAccount: boolean;
  profile?: UserProfile;
}

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profile: Partial<UserProfile> & { has_completed_onboarding?: boolean }) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      setIsLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        if (session) {
          fetchUserProfile(session.user);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        console.log("Auth state changed, event:", _event, "session:", session ? "exists" : "null");
        setSession(session);
        if (session) {
          fetchUserProfile(session.user);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error("Error setting up auth state change listener:", error);
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async (supabaseUser: User) => {
    setIsLoading(true);
    try {
      console.log("Fetching user profile for:", supabaseUser.id, "with email:", supabaseUser.email || 'unknown');
      
      // Check if we're using the fallback client or if there's no network connection
      if (isUsingFallbackClient()) {
        console.log("Using fallback client - creating local user profile");
        
        const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

        const fallbackUser: AuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          firstName: supabaseUser.user_metadata?.first_name || 'User',
          lastName: supabaseUser.user_metadata?.last_name || '',
          isEmailVerified: supabaseUser.email_confirmed_at !== null,
          hasCompletedOnboarding: isDemoAccount ? false : true,
          isAdmin: isDemoAccount,
          isDemoAccount: isDemoAccount,
          profile: {
            mentalHealthGoals: [],
            concerns: [],
            communicationStyle: 'gentle',
            privacySettings: {
              dataAnalysis: true,
              aiPersonalization: true,
              anonymousUsage: false
            }
          }
        };
        
        setUser(fallbackUser);
        setIsLoading(false);
        return;
      }
      
      try {
        // Get user profile from profiles table
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', supabaseUser.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user profile:', error.message || error, "for user ID:", supabaseUser.id);
          // If it's a network error, fall back to creating a local profile
          if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_CLOSED') || error.message?.includes('fetch')) {
            console.log("Network error detected when fetching profile, creating local profile");
            
            const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

            const networkFallbackUser: AuthUser = {
              id: supabaseUser.id,
              email: supabaseUser.email || '',
              firstName: supabaseUser.user_metadata?.first_name || 'User',
              lastName: supabaseUser.user_metadata?.last_name || '',
              isEmailVerified: supabaseUser.email_confirmed_at !== null,
              hasCompletedOnboarding: isDemoAccount ? false : true,
              isAdmin: isDemoAccount,
              isDemoAccount: isDemoAccount,
              profile: {
                mentalHealthGoals: [],
                concerns: [],
                communicationStyle: 'gentle',
                privacySettings: {
                  dataAnalysis: true,
                  aiPersonalization: true,
                  anonymousUsage: false
                }
              }
            };
            
            setUser(networkFallbackUser);
            setIsLoading(false);
            return;
          }
          throw error;
        }

        if (!data) {
          console.log("No profile found for user:", supabaseUser.id, "- creating fallback profile");
          
          const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

          const fallbackUser: AuthUser = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            firstName: supabaseUser.user_metadata?.first_name || 'User',
            lastName: supabaseUser.user_metadata?.last_name || '',
            isEmailVerified: supabaseUser.email_confirmed_at !== null,
            hasCompletedOnboarding: false,
            isAdmin: isDemoAccount,
            isDemoAccount: isDemoAccount,
            profile: {
              mentalHealthGoals: [],
              concerns: [],
              communicationStyle: 'gentle',
              privacySettings: {
                dataAnalysis: true,
                aiPersonalization: true,
                anonymousUsage: false
              }
            }
          };
          
          setUser(fallbackUser);
          
          // Try to create a profile for this user
          try {
            console.log("Attempting to create profile for user:", supabaseUser.id);
            const { error: insertError } = await supabase
              .from('profiles')
              .insert([{
                id: supabaseUser.id,
                first_name: supabaseUser.user_metadata?.first_name || 'User',
                last_name: supabaseUser.user_metadata?.last_name || '',
                email: supabaseUser.email || '',
                has_completed_onboarding: true,
                is_admin: supabaseUser.email === 'teddy.leon@alumni.uwi.edu',
                created_at: new Date(),
                updated_at: new Date()
              }]);
            
            if (insertError) {
              console.error("Failed to create profile:", insertError);
            } else {
              console.log("Successfully created profile for user:", supabaseUser.id);
              // Update the user object with the new profile
              fallbackUser.hasCompletedOnboarding = true;
              fallbackUser.isAdmin = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';
              setUser(fallbackUser);
            }
          } catch (createError) {
            console.error("Error creating profile:", createError);
          }
          console.log("Using fallback user profile:", fallbackUser);
          setIsLoading(false);
          return;
        }

        const isDemoAccount = data.is_demo_account === true;

        const authUser: AuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email || data.email || '',
          firstName: data.first_name,
          lastName: data.last_name,
          isEmailVerified: supabaseUser.email_confirmed_at !== null,
          hasCompletedOnboarding: isDemoAccount ? false : (data.has_completed_onboarding === true),
          isAdmin: data.is_admin === true,
          isDemoAccount: isDemoAccount,
          profile: {
            age: data.age,
            location: data.location,
            mentalHealthGoals: data.mental_health_goals || [],
            concerns: data.concerns || [],
            communicationStyle: data.communication_style || 'gentle',
            crisisContact: data.crisis_contact,
            privacySettings: data.privacy_settings || {
              dataAnalysis: true,
              aiPersonalization: true,
              anonymousUsage: false
            },
            phq2Score: data.phq2_score,
            created_at: data.created_at
          }
        };
        
        setUser(authUser);
        console.log("Loaded existing user profile:", authUser, "hasCompletedOnboarding:", authUser.hasCompletedOnboarding, "isAdmin:", authUser.isAdmin);
      } catch (error) {
        console.error('Error in profile fetch process:', error);
        
        // Check if it's a network error and create fallback profile
        if (error.message?.includes('Failed to fetch') || error.message?.includes('fetch') || error.message?.includes('ERR_CONNECTION_CLOSED')) {
          console.log("Network error in profile fetch process, creating fallback profile");
          
          const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

          const emergencyFallbackUser: AuthUser = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            firstName: supabaseUser.user_metadata?.first_name || 'User',
            lastName: supabaseUser.user_metadata?.last_name || '',
            isEmailVerified: supabaseUser.email_confirmed_at !== null,
            hasCompletedOnboarding: isDemoAccount ? false : true,
            isAdmin: isDemoAccount,
            isDemoAccount: isDemoAccount,
            profile: {
              mentalHealthGoals: [],
              concerns: [],
              communicationStyle: 'gentle',
              privacySettings: {
                dataAnalysis: true,
                aiPersonalization: true,
                anonymousUsage: false
              }
            }
          };
        
          setUser(emergencyFallbackUser);
          console.log("Created emergency fallback user profile due to network error:", emergencyFallbackUser);
        } else {
          console.error('Non-network error in profile fetch:', error);
          const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

          const emergencyFallbackUser: AuthUser = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            firstName: supabaseUser.user_metadata?.first_name || 'User',
            lastName: supabaseUser.user_metadata?.last_name || '',
            isEmailVerified: supabaseUser.email_confirmed_at !== null,
            hasCompletedOnboarding: false,
            isAdmin: isDemoAccount,
            isDemoAccount: isDemoAccount,
            profile: {
              mentalHealthGoals: [],
              concerns: [],
              communicationStyle: 'gentle',
              privacySettings: {
                dataAnalysis: true,
                aiPersonalization: true,
                anonymousUsage: false
              }
            }
          };
        
          setUser(emergencyFallbackUser);
        }
      }
    } catch (err) {
      console.error('Error in fetchUserProfile:', err);
      
      // Check if it's a network error
      if (err.message?.includes('Failed to fetch') || err.message?.includes('fetch') || err.message?.includes('ERR_CONNECTION_CLOSED')) {
        console.log("Network connectivity issue detected, using offline mode");
        setError('Network connection issue. Running in offline mode.');
        
        const isDemoAccount = supabaseUser.email === 'teddy.leon@alumni.uwi.edu';

        const offlineFallbackUser: AuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          firstName: supabaseUser.user_metadata?.first_name || 'User',
          lastName: supabaseUser.user_metadata?.last_name || '',
          isEmailVerified: supabaseUser.email_confirmed_at !== null,
          hasCompletedOnboarding: isDemoAccount ? false : true,
          isAdmin: isDemoAccount,
          isDemoAccount: isDemoAccount,
          profile: {
            mentalHealthGoals: [],
            concerns: [],
            communicationStyle: 'gentle',
            privacySettings: {
              dataAnalysis: true,
              aiPersonalization: true,
              anonymousUsage: false
            }
          }
        };
        
        setUser(offlineFallbackUser);
      } else {
        setError('Failed to fetch user profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    console.log("Starting login process for:", email);
    try {
      console.log("Attempting login for:", email);
      
      // Enhanced error handling for network issues
      try {
        const { data, error } = await supabase.auth.signInWithPassword({ 
          email, 
          password 
        });
      
        if (error) {
          console.error('Supabase auth error:', error);
          // Provide more user-friendly error messages
          if (error.message.includes('Invalid login credentials')) {
            throw new Error('No account found with these credentials. Please check your email and password, or create a new account if you don\'t have one yet.');
          } else if (error.message.includes('Email not confirmed')) {
            throw new Error('Please verify your email before logging in. Check your inbox for a verification link.');
          } else if (error.message.includes('Too many requests')) {
            throw new Error('Too many login attempts. Please wait a few minutes before trying again.');
          }
          throw error;
        }
        
        // More comprehensive checks for the auth response
        if (!data) {
          console.error('No data returned from Supabase auth');
          throw new Error('Authentication failed: No response data');
        }
        
        // Type guard to ensure data has user property
        if (!('user' in data) || !data.user) {
          console.error('No user data in auth response:', data);
          throw new Error('Authentication failed: No user data returned');
        }
        
        // Check if email is verified - with additional null checks
        const emailConfirmedAt = data.user.email_confirmed_at;
        if (emailConfirmedAt === null || emailConfirmedAt === undefined) {
          console.log('Email not verified for user:', data.user.email);
          // Sign out the user since email is not verified
          await supabase.auth.signOut();
          setError('Please verify your email before logging in. Check your inbox for a verification link.');
          throw new Error('Email not verified');
        }
        
        // Don't set isLoading to false here - let the auth state change handler do it
        // after it fetches the user profile
        console.log('Login successful for user:', data.user.email);
      } catch (networkError) {
        if (networkError.message?.includes('fetch') || networkError.message?.includes('network')) {
          console.error('Network error during login:', networkError);
          throw new Error('Network connection failed. Please check your internet connection and try again.');
        }
        throw networkError;
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in';
      setError(errorMessage);
      setIsLoading(false);
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      try {
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName
            },
            emailRedirectTo: `${window.location.origin}/auth`
          }
        });
        
        if (error) {
          console.error('Registration error:', error);
          // Provide more user-friendly error messages
          if (error.message.includes('User already registered')) {
            throw new Error('An account with this email already exists. Please try logging in instead.');
          } else if (error.message.includes('Password should be at least')) {
            throw new Error('Password must be at least 6 characters long.');
          } else if (error.message.includes('Invalid email')) {
            throw new Error('Please enter a valid email address.');
          }
          throw error;
        }
      } catch (networkError) {
        if (networkError.message?.includes('fetch') || networkError.message?.includes('network')) {
          console.error('Network error during registration:', networkError);
          throw new Error('Network connection failed. Please check your internet connection and try again.');
        }
        throw networkError;
      }
      
      // Profile creation is now handled by the database trigger
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign up';
      setError(errorMessage);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      // Clear session storage flags for dashboard visits
      if (user) {
        const sessionKey = `dashboard_visited_${user.id}`;
        sessionStorage.removeItem(sessionKey);
        console.log("Cleared dashboard visit flag for user:", user.id);
      }
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profile: Partial<UserProfile> & { has_completed_onboarding?: boolean }) => {
    if (!user) return;
    
    setIsLoading(true); 
    setError(null);
    try {
      console.log("Updating profile for user:", user.id, profile);
      
      // Map profile data to database structure
      const profileData: any = {};
      
      // Handle age field - convert to null if invalid
      if (profile.age !== undefined) {
        if (profile.age === null || profile.age === undefined || profile.age === '' || String(profile.age).trim() === '') {
          profileData.age = null;
        } else {
          const ageNum = Number(profile.age);
          profileData.age = isNaN(ageNum) ? null : ageNum;
        }
      }
      
      if (profile.location !== undefined) profileData.location = profile.location;
      if (profile.mentalHealthGoals !== undefined) profileData.mental_health_goals = profile.mentalHealthGoals;
      if (profile.concerns !== undefined) profileData.concerns = profile.concerns;
      if (profile.communicationStyle !== undefined) profileData.communication_style = profile.communicationStyle;
      if (profile.crisisContact !== undefined) profileData.crisis_contact = profile.crisisContact;
      if (profile.privacySettings !== undefined) profileData.privacy_settings = profile.privacySettings;
      
      // Handle phq2Score field - convert to null if invalid
      if (profile.phq2Score !== undefined) {
        if (profile.phq2Score === null || profile.phq2Score === undefined || profile.phq2Score === '' || String(profile.phq2Score).trim() === '') {
          profileData.phq2_score = null;
        } else {
          const scoreNum = Number(profile.phq2Score);
          profileData.phq2_score = isNaN(scoreNum) ? null : scoreNum;
        }
      }
      
      // Mark onboarding as completed if specified (but not for demo accounts)
      if (profile.has_completed_onboarding !== undefined) {
        if (!user.isDemoAccount) {
          profileData.has_completed_onboarding = profile.has_completed_onboarding;
        } else {
          console.log("Skipping onboarding completion save for demo account");
        }
      }

      profileData.updated_at = new Date();

      console.log("Updating profile data:", profileData);

      try {
        // Update profile in database
        const { error } = await supabase
          .from('profiles')
          .update({...profileData, updated_at: new Date()})
          .eq('id', user.id);

        if (error) {
          if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_CLOSED') || error.message?.includes('fetch')) {
            console.log("Network error during profile update, updating local state only");
            // Continue with local state update even if database update fails
          } else {
            throw error;
          }
          console.error("Error updating profile in database:", error);
        }
        
        console.log("Profile updated successfully in database");
      } catch (dbError) {
        console.error("Database error updating profile:", dbError);
        // If it's a network error, just update the local state
        if (dbError.message?.includes('Failed to fetch') || dbError.message?.includes('ERR_CONNECTION_CLOSED') || dbError.message?.includes('fetch')) {
          console.log("Network error during profile update, updating local state only");
          // Continue with local state update even if database update fails
        } else {
          throw dbError;
        }
      }
      
      // Update local user state
      setUser(prev => {
        if (!prev) return null;

        const updatedProfile = {
          ...prev.profile,
          ...profile,
          mentalHealthGoals: profile.mentalHealthGoals ?? prev.profile?.mentalHealthGoals ?? [],
          concerns: profile.concerns ?? prev.profile?.concerns ?? [],
          privacySettings: profile.privacySettings ?? prev.profile?.privacySettings ?? {
            dataAnalysis: true,
            aiPersonalization: true,
            anonymousUsage: false
          },
          communicationStyle: profile.communicationStyle ?? prev.profile?.communicationStyle ?? "gentle"
        };

        return {
          ...prev,
          hasCompletedOnboarding: prev.isDemoAccount
            ? (profile.has_completed_onboarding ?? prev.hasCompletedOnboarding)
            : (profile.has_completed_onboarding ?? prev.hasCompletedOnboarding),
          profile: updatedProfile
        };
      });
      
      console.log("Local user state updated");
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      }).catch((networkError) => {
        console.error('Network error during email resend:', networkError);
        throw new Error('Network connection failed. Please check your internet connection and try again.');
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Resend verification error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      }).catch((networkError) => {
        console.error('Network error during password reset:', networkError);
        throw new Error('Network connection failed. Please check your internet connection and try again.');
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset password email';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateProfile,
    resendVerification,
    resetPassword,
    isLoading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};